import {
  type CalendarDate,
  DateFormatter,
  getLocalTimeZone,
  parseDate,
  today,
  type DateValue,
} from '@internationalized/date';

/**
 * The domain date layer (backbone §0.7 / Addendum B.2).
 *
 * Effective dates in insurance are **civil dates** — "the cover starts on 2026-01-01" is the same
 * day in Hannover and in Honolulu. Using `Date` for them silently reintroduces a timezone, which
 * is how off-by-one-day bugs get into policy terms. Domain dates therefore never touch `new Date()`;
 * they are `CivilDate` strings parsed through `@internationalized/date`.
 */

/** ISO `YYYY-MM-DD`. The only representation of a domain date on the wire and in state. */
export type CivilDate = string;

const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isCivilDate(value: unknown): value is CivilDate {
  if (typeof value !== 'string' || !CIVIL_DATE_PATTERN.test(value)) return false;
  try {
    parseDate(value);
    return true;
  } catch {
    return false;
  }
}

export function toCalendarDate(value: CivilDate): CalendarDate {
  if (!isCivilDate(value)) throw new Error(`Not a civil date: "${String(value)}".`);
  return parseDate(value);
}

export function fromCalendarDate(value: DateValue): CivilDate {
  return `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
}

/** "Today" is a civil date in the user's zone — the only sanctioned entry point for `now`. */
export function civilToday(timeZone: string = getLocalTimeZone()): CivilDate {
  return fromCalendarDate(today(timeZone));
}

export function compareCivilDates(a: CivilDate, b: CivilDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export interface ValidityWindow {
  readonly validFrom: CivilDate | null;
  readonly validTo?: CivilDate | null;
}

/** Half-open `[validFrom, validTo]` inclusive on both ends — insurance cover ends *on* validTo. */
export function isWithinValidity(window: ValidityWindow, asOf: CivilDate): boolean {
  if (window.validFrom !== null && compareCivilDates(asOf, window.validFrom) < 0) return false;
  if (window.validTo != null && compareCivilDates(asOf, window.validTo) > 0) return false;
  return true;
}

export function addMonths(value: CivilDate, months: number): CivilDate {
  return fromCalendarDate(toCalendarDate(value).add({ months }));
}

/** `days` may be negative: the last day of a half-open segment is `addDays(validTo, -1)`. */
export function addDays(value: CivilDate, days: number): CivilDate {
  return fromCalendarDate(toCalendarDate(value).add({ days }));
}

/** Whole days from `from` to `to` (negative when `to` is earlier) — for laying dates on a scale. */
export function daysBetween(from: CivilDate, to: CivilDate): number {
  return toCalendarDate(to).compare(toCalendarDate(from));
}

export function endOfMonth(value: CivilDate): CivilDate {
  const date = toCalendarDate(value);
  return fromCalendarDate(date.set({ day: date.calendar.getDaysInMonth(date) }));
}

/** Presentation only — never used to derive domain values. */
export function formatCivilDate(
  value: CivilDate,
  locale: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string {
  return new DateFormatter(locale, options).format(
    toCalendarDate(value).toDate(getLocalTimeZone()),
  );
}

/** `2026-07` → a localized month label, for billing periods. */
export function formatPeriod(period: string, locale: string): string {
  const [year, month] = period.split('-');
  if (year === undefined || month === undefined) return period;
  return formatCivilDate(`${year}-${month}-01`, locale, { month: 'long', year: 'numeric' });
}

/**
 * UI language → formatting locale.
 *
 * The language switcher offers `de` and `en`, but a bare `en` resolves to `en-US`, which renders
 * 2025-02-01 as "02/01/2025" — read as 1 February by every user of this product and as 2 January
 * by `Intl`. The legacy admin made the same mapping (`en → en-gb`) for the same reason.
 *
 * Formatting locale is a presentation concern and deliberately separate from the i18n language:
 * translations are keyed by language, number and date shapes by locale.
 */
export function resolveFormattingLocale(language: string): string {
  const base = language.split('-')[0];
  switch (base) {
    case 'de':
      return 'de-DE';
    case 'en':
      return 'en-GB';
    default:
      return language;
  }
}
